import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserRound, Loader2, Copy } from "lucide-react";
import { api } from "@/lib/api";

export const validPhone = (v) => {
  let d = (v || "").replace(/\D/g, "");
  if (d.startsWith("233") && d.length === 12) d = "0" + d.slice(3);
  if (d.length === 9) d = "0" + d;
  return d.length === 10 && d.startsWith("0");
};

export const looseValidPhone = (v) => {
  const d = (v || "").replace(/\D/g, "");
  return d.length >= 7 && d.length <= 15;
};

export const passengerErrors = (list, insurance = false) =>
  list.map((p) => (insurance ? {
    name: (p.name || "").trim().length < 2 ? "Name is required" : "",
    phone: !validPhone(p.phone) ? "Insurance needs a valid Ghana mobile (e.g. 024 123 4567)" : "",
    gender: !p.gender ? "Select gender" : "",
    age: !(Number(p.age) > 0) ? "Age is required for insurance" : "",
    kin_name: (p.kin_name || "").trim().length < 2 ? "Next of kin name required" : "",
    kin_phone: !validPhone(p.kin_phone) ? "Valid Ghana next of kin mobile required" : "",
  } : {
    name: (p.name || "").trim().length < 2 ? "Name is required" : "",
    phone: !looseValidPhone(p.phone) ? "Enter a valid phone number" : "",
    gender: "", age: "", kin_name: "", kin_phone: "",
  }));

const GENDERS = [["male", "Male"], ["female", "Female"], ["other", "Other"]];

export default function PassengerForm({ passengers, setPassengers, errors, showErrors, insurance = false }) {
  const [looking, setLooking] = useState({});
  const update = (i, patch) => setPassengers((prev) => prev.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  // Multiple tickets: next-of-kin is taken from Passenger 1 for everyone (hidden on later rows to save space).
  useEffect(() => {
    if (passengers.length < 2) return;
    const kn = passengers[0]?.kin_name || "";
    const kp = passengers[0]?.kin_phone || "";
    if (passengers.some((p, i) => i >= 1 && (p.kin_name !== kn || p.kin_phone !== kp))) {
      setPassengers((prev) => prev.map((p, i) => (i >= 1 ? { ...p, kin_name: kn, kin_phone: kp } : p)));
    }
  }, [passengers, setPassengers]);

  const copyFromFirst = (i) => {
    const f = passengers[0] || {};
    update(i, { phone: f.phone || "" });
  };
  const first = passengers[0] || {};
  const firstPhoneReady = (first.phone || "").replace(/\D/g, "").length >= 7;

  const lookup = async (i, phone, isKin) => {
    if (!validPhone(phone)) return;
    const key = `${i}-${isKin ? "kin" : "self"}`;
    setLooking((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.get(`/public/passenger-lookup`, { params: { phone } });
      if (data.found) {
        setPassengers((prev) => prev.map((p, j) => {
          if (j !== i) return p;
          const np = { ...p };
          if (isKin) {
            if (!(p.kin_name || "").trim() && data.name) np.kin_name = data.name;
          } else {
            if (data.name && !(p.name || "").trim()) np.name = data.name;
            if (data.gender && !p.gender) np.gender = data.gender;
            if (data.age != null && !p.age) np.age = data.age;
            if (data.kin_name && !(p.kin_name || "").trim()) np.kin_name = data.kin_name;
            if (data.kin_phone && !(p.kin_phone || "").trim()) np.kin_phone = data.kin_phone;
          }
          return np;
        }));
      }
    } catch { /* lookup is best-effort */ }
    finally { setLooking((s) => ({ ...s, [key]: false })); }
  };

  return (
    <div className="space-y-4">
      {insurance && <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800" data-testid="insurance-fields-note">Insurance selected: each passenger needs a valid Ghana mobile (passenger &amp; next of kin), age, gender and next-of-kin name. Don't have these? Turn off insurance to book the ticket anyway.</p>}
      {passengers.map((p, i) => {
        const e = showErrors ? errors[i] : {};
        return (
          <div key={p.seat} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`passenger-block-${p.seat}`}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-display font-bold text-slate-800">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#0052FF] text-sm text-white">{p.seat}</span>
                <UserRound className="h-4 w-4 text-slate-400" /> Passenger {i + 1}
              </div>
              {i >= 1 && (
                <button type="button" disabled={!firstPhoneReady} onClick={() => copyFromFirst(i)} data-testid={`passenger-copy-first-${p.seat}`}
                  title={firstPhoneReady ? "Copy Passenger 1's phone" : "Fill Passenger 1's phone first"}
                  className="inline-flex items-center gap-1 rounded-full border border-[#0052FF]/30 bg-blue-50 px-3 py-1 text-xs font-bold text-[#0052FF] hover:bg-blue-100 btn-press disabled:cursor-not-allowed disabled:opacity-40">
                  <Copy className="h-3 w-3" />Same phone as Passenger 1
                </button>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Mobile number *" err={e.phone} busy={looking[`${i}-self`]}>
                <Input value={p.phone} inputMode="tel" onChange={(x) => update(i, { phone: x.target.value })} onBlur={(x) => lookup(i, x.target.value, false)} placeholder="024 123 4567" className="bg-white" data-testid={`passenger-phone-input-${p.seat}`} />
              </Field>
              <Field label="Full name *" err={e.name}>
                <Input value={p.name} onChange={(x) => update(i, { name: x.target.value })} placeholder="e.g. Ama Mensah" className="bg-white" data-testid={`passenger-name-input-${p.seat}`} />
              </Field>
              <Field label={`Gender ${insurance ? "*" : "(optional)"}`} err={e.gender}>
                <div className="flex gap-2">
                  {GENDERS.map(([val, lbl]) => (
                    <button key={val} type="button" onClick={() => update(i, { gender: val })} data-testid={`passenger-gender-${val}-${p.seat}`}
                      className={`flex-1 rounded-lg border-2 px-2 py-2 text-xs font-bold transition-colors ${p.gender === val ? "border-[#0052FF] bg-blue-50 text-[#0052FF]" : "border-slate-200 bg-white text-slate-600 hover:border-blue-200"}`}>{lbl}</button>
                  ))}
                </div>
              </Field>
              <Field label={`Age ${insurance ? "*" : "(optional)"}`} err={e.age}>
                <Input value={p.age || ""} inputMode="numeric" onChange={(x) => update(i, { age: x.target.value.replace(/\D/g, "").slice(0, 3) })} placeholder="0" className="bg-white" data-testid={`passenger-age-input-${p.seat}`} />
              </Field>
              {i === 0 ? (
                <>
                  <Field label={`Next of kin mobile ${insurance ? "*" : "(optional)"}`} err={e.kin_phone} busy={looking[`${i}-kin`]}>
                    <Input value={p.kin_phone} inputMode="tel" onChange={(x) => update(i, { kin_phone: x.target.value })} onBlur={(x) => lookup(i, x.target.value, true)} placeholder="024 765 4321" className="bg-white" data-testid={`passenger-kin-phone-input-${p.seat}`} />
                  </Field>
                  <Field label={`Next of kin name ${insurance ? "*" : "(optional)"}`} err={e.kin_name}>
                    <Input value={p.kin_name} onChange={(x) => update(i, { kin_name: x.target.value })} placeholder="e.g. Kofi Mensah" className="bg-white" data-testid={`passenger-kin-name-input-${p.seat}`} />
                  </Field>
                </>
              ) : (
                <div className="flex items-center rounded-lg bg-blue-50/70 px-3 py-2 text-xs font-medium text-slate-500 sm:col-span-2" data-testid={`passenger-kin-inherited-${p.seat}`}>
                  Next of kin: same as Passenger 1{p.kin_name ? ` · ${p.kin_name}` : ""}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const Field = ({ label, err, busy, children }) => (
  <div className="space-y-1.5">
    <Label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">{label}{busy && <Loader2 className="h-3 w-3 animate-spin text-[#0052FF]" />}</Label>
    {children}
    {err && <p className="text-xs font-medium text-rose-600">{err}</p>}
  </div>
);
